import { useEffect, useState } from "react";
import { Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { PageHeader, Field } from "@/components/admin/ui";

export default function Media() {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sel, setSel] = useState(null);
  const load = () => api.get("/admin/media").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData(); fd.append("file", file);
      try { await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } }); }
      catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    }
    setUploading(false); e.target.value = ""; toast.success("Upload complete"); load();
  };
  const saveMeta = async () => { await api.put(`/admin/media/${sel.id}`, sel); toast.success("Saved"); load(); };
  const del = async (id) => { if (!window.confirm("Delete this file?")) return; await api.delete(`/admin/media/${id}`); setSel(null); toast.success("Deleted"); load(); };

  return (
    <div data-testid="admin-media">
      <PageHeader title="Media Library" subtitle="Upload and manage images (max 8MB — jpg, png, gif, webp, svg)">
        <label className="inline-flex items-center gap-2 rounded-lg bg-emerald-800 text-amber-50 px-4 py-2 text-sm font-semibold cursor-pointer hover:bg-emerald-900" data-testid="media-page-upload-btn">
          <Upload size={16} /> {uploading ? "Uploading…" : "Upload Images"}
          <input type="file" accept="image/*" multiple className="hidden" onChange={upload} disabled={uploading} />
        </label>
      </PageHeader>
      <div className="grid lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.length === 0 && <p className="col-span-full text-center text-stone-400 py-16">No media yet.</p>}
          {items.map((m) => (
            <button key={m.id} onClick={() => setSel(m)} className={`rounded-xl overflow-hidden border aspect-square ${sel?.id === m.id ? "ring-2 ring-emerald-600" : "border-stone-200"}`} data-testid={`media-item-${m.id}`}>
              <img src={fileUrl(m.storage_path)} alt={m.alt || m.original_filename} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
        <div className="bg-white rounded-2xl border border-stone-200/80 p-5 h-fit">
          {sel ? (
            <div className="space-y-3">
              <img src={fileUrl(sel.storage_path)} alt={sel.alt} className="w-full rounded-lg object-cover" />
              <p className="text-xs text-stone-500 font-mono break-all">{sel.original_filename}</p>
              <Field label="Alt text" value={sel.alt} onChange={(v) => setSel({ ...sel, alt: v })} />
              <Field label="Caption" value={sel.caption} onChange={(v) => setSel({ ...sel, caption: v })} />
              <div className="flex gap-2">
                <button onClick={saveMeta} className="flex-1 rounded-lg bg-emerald-800 text-amber-50 py-2 text-sm font-semibold hover:bg-emerald-900">Save</button>
                <button onClick={() => del(sel.id)} className="rounded-lg bg-red-50 text-red-600 px-3 py-2 hover:bg-red-100"><Trash2 size={16} /></button>
              </div>
            </div>
          ) : <p className="text-sm text-stone-400 text-center py-10">Select an image to edit its details.</p>}
        </div>
      </div>
    </div>
  );
}
